"use client";

import { ChevronDown } from "lucide-react";
import { usePathname } from "next/navigation";
import { usePostHog } from "posthog-js/react";
import { useEffect, useId, useRef, useState } from "react";
import type { NavLink } from "./nav-links";
import { ZoneLink } from "./zone-link";

type NavDropdownProps = {
	label: string;
	items: NavLink[];
};

const triggerBaseClass =
	"inline-flex cursor-pointer items-center gap-1 rounded-xl px-3 py-2 font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background";

// Desktop-only nav dropdown (Explore / For Business / Data). Accessibility
// mechanics copied from profile-dropdown.tsx (click-open, outside-click +
// Escape close). Unlike profile-dropdown, the panel markup is ALWAYS rendered
// (never `{open && <panel>}`) and only toggled via the `hidden` attribute —
// Googlebot renders JS anyway, but keeping the real <a href> anchors in the
// initial SSR HTML guarantees they are crawlable even if JS never runs, and
// avoids a hydration content-swap.
//
// PR-3 (2026-08-10, Task 3.4): this used to announce `role="menu"` /
// `role="menuitem"` — the WAI-ARIA menu pattern's keyboard contract (arrow
// keys move between items, Home/End jump to the ends). Zero arrow-key
// handlers existed here or anywhere in this file, so a screen-reader user
// was told a contract the component never honored. Converted to the
// disclosure pattern instead: the trigger keeps `aria-haspopup` (as the
// generic `"true"`, since the panel below no longer claims a `menu` role) /
// `aria-expanded` / `aria-controls`; the panel is a plain container of plain
// links. Escape-closes-and-returns-focus and outside-click-closes are
// unaffected — those aren't part of the menu pattern's contract, they're
// this component's own behavior.
export function NavDropdown({ label, items }: NavDropdownProps) {
	const pathname = usePathname();
	const posthog = usePostHog();
	const [open, setOpen] = useState(false);
	const containerRef = useRef<HTMLDivElement>(null);
	const triggerRef = useRef<HTMLButtonElement>(null);
	const menuId = useId();

	const isActive = items.some(
		(item) => pathname === item.href || pathname.startsWith(`${item.href}/`),
	);

	useEffect(() => {
		if (!open) return;
		function onClick(event: MouseEvent) {
			if (
				containerRef.current &&
				!containerRef.current.contains(event.target as Node)
			) {
				setOpen(false);
			}
		}
		function onKey(event: KeyboardEvent) {
			if (event.key === "Escape") {
				setOpen(false);
				triggerRef.current?.focus();
			}
		}
		document.addEventListener("mousedown", onClick);
		document.addEventListener("keydown", onKey);
		return () => {
			document.removeEventListener("mousedown", onClick);
			document.removeEventListener("keydown", onKey);
		};
	}, [open]);

	function toggleOpen() {
		setOpen((prev) => {
			const next = !prev;
			if (next) {
				posthog?.capture("nav_dropdown_opened", { group: label });
			}
			return next;
		});
	}

	return (
		<div ref={containerRef} className="relative inline-flex items-center">
			<button
				ref={triggerRef}
				type="button"
				onClick={toggleOpen}
				aria-haspopup="true"
				aria-expanded={open}
				aria-controls={menuId}
				className={`${triggerBaseClass} ${
					isActive
						? "bg-accent text-foreground"
						: "hover:bg-accent hover:text-foreground"
				}`}
			>
				{label}
				<ChevronDown
					className={`h-3.5 w-3.5 transition-transform ${
						open ? "rotate-180" : ""
					}`}
					aria-hidden="true"
				/>
			</button>

			{/* `whitespace-normal` below is load-bearing: the parent <nav> in
			    nav-links.tsx sets `whitespace-nowrap` (correct for its single-line
			    trigger row) and `white-space` inherits, so without this reset the
			    item descriptions render as one unwrapped line. Measured live
			    2026-08-11 on /id/remote-jobs/design: the "Pasang Iklan"
			    description laid out 386px wide inside the 288px (`w-72`) panel,
			    spilling ~119px past the card's right edge with nothing clipping
			    it. */}
			<div
				id={menuId}
				data-testid="nav-dropdown-panel"
				hidden={!open}
				className="absolute left-0 top-full z-50 mt-2 w-72 whitespace-normal rounded-2xl border border-border bg-popover p-2 shadow-lg"
			>
				{items.map((item, index) => {
					const itemIsActive =
						pathname === item.href || pathname.startsWith(`${item.href}/`);
					return (
						<ZoneLink
							key={item.href}
							href={item.href}
							aria-current={itemIsActive ? "page" : undefined}
							onClick={() => setOpen(false)}
							className={`flex w-full flex-col items-start gap-0.5 rounded-xl px-3 py-2.5 transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background ${
								index === 0 && items.length > 1
									? "mb-1 border-b border-border pb-3"
									: ""
							}`}
						>
							<span
								className={`text-sm text-foreground ${
									index === 0 ? "font-semibold" : "font-medium"
								}`}
							>
								{item.label}
							</span>
							{item.description && (
								<span className="text-xs leading-snug text-muted-foreground">
									{item.description}
								</span>
							)}
						</ZoneLink>
					);
				})}
			</div>
		</div>
	);
}
